import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from 'react-i18next';
import { useKeyValuePairs } from '../hooks/useKeyValuePairs';
import { isValidSipDialString } from '@shared/utils/sipDialString';

interface KeyValuePair {
  key: string;
  value: string;
}

interface ValidationErrors {
  keyErrors: Record<number, string>;
  valueErrors: Record<number, string>;
}

interface EditAttributesMetadataModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    attributes?: Record<string, string>,
    metadata?: Record<string, string>,
    phoneNumber?: string,
  ) => void;
  mode?: 'add' | 'edit';
  initialPhoneNumber?: string;
  initialAttributes?: Record<string, string>;
  initialMetadata?: Record<string, string>;
  phoneNumberLocked?: boolean;
  phoneNumberLockedHint?: string;
}

const createDefaultAddAttributes = (): KeyValuePair[] => [
  { key: 'name', value: '' },
  { key: 'gender', value: '' },
];

const FIXED_ATTRIBUTE_KEYS = new Set(['name', 'gender']);

const primaryButtonSx = {
  px: 1.75,
  py: 1.25,
  fontSize: '14px',
  bgcolor: '#1875FF',
  color: 'common.white',
  '&:hover': { bgcolor: '#005EE9' },
};

const deleteButtonSx = {
  bgcolor: '#FFF4F1',
  borderRadius: '12px',
  width: '40px',
  height: '40px',
  flexShrink: 0,
  '&:hover': { bgcolor: '#FFF4F1' },
};

export const EditAttributesMetadataModal: React.FC<EditAttributesMetadataModalProps> = ({
  open,
  onClose,
  onSave,
  mode = 'edit',
  initialPhoneNumber = '',
  initialAttributes,
  initialMetadata,
  phoneNumberLocked = false,
  phoneNumberLockedHint,
}) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [phoneError, setPhoneError] = useState('');

  const attributesManager = useKeyValuePairs(initialAttributes);
  const metadataManager = useKeyValuePairs(initialMetadata);

  const resetForm = () => {
    setPhoneNumber('');
    setPhoneError('');
    attributesManager.setPairs(createDefaultAddAttributes());
    metadataManager.setPairs([]);
    attributesManager.setKeyErrors({});
    attributesManager.setValueErrors({});
    metadataManager.setKeyErrors({});
    metadataManager.setValueErrors({});
  };

  const initForm = () => {
    setPhoneNumber(initialPhoneNumber);
    setPhoneError('');

    if (mode === 'add') {
      attributesManager.setPairs(createDefaultAddAttributes());
      metadataManager.setPairs([]);
    } else {
      const existingPairs = initialAttributes
        ? Object.entries(initialAttributes).map(([key, value]) => ({ key, value }))
        : [];

      const fixedPairs = Array.from(FIXED_ATTRIBUTE_KEYS).map((key) => ({
        key,
        value: initialAttributes?.[key] ?? '',
      }));
      const otherPairs = existingPairs.filter((p) => !FIXED_ATTRIBUTE_KEYS.has(p.key));

      attributesManager.setPairs([...fixedPairs, ...otherPairs]);
      metadataManager.setPairs(
        initialMetadata
          ? Object.entries(initialMetadata).map(([key, value]) => ({ key, value }))
          : [],
      );
    }

    attributesManager.setKeyErrors({});
    attributesManager.setValueErrors({});
    metadataManager.setKeyErrors({});
    metadataManager.setValueErrors({});
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    initForm();
  }, [open, initialPhoneNumber, initialAttributes, initialMetadata, mode]);

  const validatePhoneNumber = (value: string): string => {
    if (!value.trim()) return t('phoneNumbers.validation.phoneRequired');
    if (!isValidSipDialString(value)) return t('phoneNumbers.validation.phoneInvalid');
    return '';
  };

  const validateKeyValuePairs = (pairs: KeyValuePair[]): ValidationErrors => {
    const keyErrors: Record<number, string> = {};
    const valueErrors: Record<number, string> = {};
    const keysSeen = new Set<string>();

    pairs.forEach((pair, index) => {
      if (pair.key.trim() === '' && pair.value.trim() === '') return;

      const trimmedKey = pair.key.trim();
      const trimmedValue = pair.value.trim();

      if (trimmedKey === '') {
        keyErrors[index] = t('phoneNumbers.editCustomerData.validation.keyRequired');
      } else if (pair.key.includes(' ')) {
        keyErrors[index] = t('phoneNumbers.editCustomerData.validation.keyNoSpaces');
      } else if (keysSeen.has(trimmedKey)) {
        keyErrors[index] = t('phoneNumbers.editCustomerData.validation.duplicateKey');
      } else {
        keysSeen.add(trimmedKey);
      }

      if (trimmedKey !== '' && trimmedValue === '' && !FIXED_ATTRIBUTE_KEYS.has(trimmedKey)) {
        valueErrors[index] = t('phoneNumbers.editCustomerData.validation.valueRequired');
      } else if (trimmedKey !== '' && trimmedValue !== '' && pair.value.includes(' ')) {
        valueErrors[index] = t('phoneNumbers.editCustomerData.validation.valueNoSpaces');
      }
    });

    return { keyErrors, valueErrors };
  };

  const handleSave = () => {
    if (!phoneNumberLocked) {
      const err = validatePhoneNumber(phoneNumber);
      if (err) {
        setPhoneError(err);
        return;
      }
    }

    const filledAttributes = attributesManager.pairs.filter(
      (p) => FIXED_ATTRIBUTE_KEYS.has(p.key.trim()) || p.key.trim() !== '' || p.value.trim() !== '',
    );
    const filledMetadata = metadataManager.pairs.filter(
      (p) => p.key.trim() !== '' || p.value.trim() !== '',
    );

    const attrValidation = validateKeyValuePairs(filledAttributes);
    const metaValidation = validateKeyValuePairs(filledMetadata);

    const hasErrors =
      Object.keys(attrValidation.keyErrors).length > 0 ||
      Object.keys(attrValidation.valueErrors).length > 0 ||
      Object.keys(metaValidation.keyErrors).length > 0 ||
      Object.keys(metaValidation.valueErrors).length > 0;

    if (hasErrors) {
      attributesManager.setKeyErrors(attrValidation.keyErrors);
      attributesManager.setValueErrors(attrValidation.valueErrors);
      metadataManager.setKeyErrors(metaValidation.keyErrors);
      metadataManager.setValueErrors(metaValidation.valueErrors);
      return;
    }

    const attributesObj = filledAttributes
      .filter((p) => p.key.trim() !== '' && p.value.trim() !== '')
      .reduce(
        (acc, p) => {
          acc[p.key.trim()] = p.value;
          return acc;
        },
        {} as Record<string, string>,
      );

    const metadataObj = filledMetadata
      .filter((p) => p.key.trim() !== '')
      .reduce(
        (acc, p) => {
          acc[p.key.trim()] = p.value;
          return acc;
        },
        {} as Record<string, string>,
      );

    onSave(
      Object.keys(attributesObj).length > 0 ? attributesObj : undefined,
      Object.keys(metadataObj).length > 0 ? metadataObj : undefined,
      phoneNumber.trim(),
    );
    if (mode === 'add') {
      resetForm();
    }
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const hasFormErrors = () =>
    Object.keys(attributesManager.keyErrors).length > 0 ||
    Object.keys(attributesManager.valueErrors).length > 0 ||
    Object.keys(metadataManager.keyErrors).length > 0 ||
    Object.keys(metadataManager.valueErrors).length > 0;

  const title = mode === 'add' ? 'Add Customer' : t('phoneNumbers.editCustomerData.title');
  const saveLabel = mode === 'add' ? 'Add' : t('common.save');

  const renderKeyValueSection = (
    manager: ReturnType<typeof useKeyValuePairs>,
    sectionTitle: string,
    description: string,
    addLabel: string,
    isAttributesSection = false,
  ) => (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ flex: 1, maxWidth: '575px' }}>
          <Typography sx={{ mb: 0.75, fontSize: '16px', fontWeight: 600 }}>
            {sectionTitle}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
            {description}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={manager.add}
          variant="contained"
          sx={{ ...primaryButtonSx, ml: 2, flexShrink: 0 }}>
          {addLabel}
        </Button>
      </Box>

      <Box sx={{ mt: 2, minHeight: '74px' }}>
        {manager.pairs.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {sectionTitle === t('phoneNumbers.editCustomerData.attributes.title')
                ? t('phoneNumbers.editCustomerData.attributes.emptyState')
                : t('phoneNumbers.editCustomerData.metadata.emptyState')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.875, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, display: 'flex', gap: 1.875 }}>
                <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>
                  {t('phoneNumbers.editCustomerData.placeholders.key')}
                </Typography>
                <Typography sx={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>
                  {t('phoneNumbers.editCustomerData.placeholders.value')}
                </Typography>
              </Box>
              <Box sx={{ width: '40px', flexShrink: 0 }} />
            </Box>
            {manager.pairs.map((pair, index) => {
              const isFixed = isAttributesSection && FIXED_ATTRIBUTE_KEYS.has(pair.key);
              const isGender = isAttributesSection && pair.key === 'gender';
              return (
                <Box key={index} sx={{ display: 'flex', gap: 1.875, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, display: 'flex', gap: 1.875 }}>
                    <TextField
                      size="small"
                      placeholder={t('phoneNumbers.editCustomerData.placeholders.key')}
                      value={pair.key}
                      onChange={(e) => manager.update(index, 'key', e.target.value)}
                      error={!!manager.keyErrors[index]}
                      helperText={manager.keyErrors[index]}
                      disabled={isFixed}
                      sx={{ flex: 1 }}
                    />
                    {isGender ? (
                      <FormControl
                        size="small"
                        sx={{ flex: 1 }}
                        error={!!manager.valueErrors[index]}>
                        <Select
                          value={pair.value}
                          onChange={(e) => manager.update(index, 'value', e.target.value)}
                          displayEmpty
                          renderValue={(val) => val || ''}>
                          <MenuItem value="">
                            <em>Not selected</em>
                          </MenuItem>
                          <MenuItem value="male">male</MenuItem>
                          <MenuItem value="female">female</MenuItem>
                        </Select>
                        {manager.valueErrors[index] && (
                          <FormHelperText>{manager.valueErrors[index]}</FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <TextField
                        size="small"
                        placeholder={t('phoneNumbers.editCustomerData.placeholders.value')}
                        value={pair.value}
                        onChange={(e) => manager.update(index, 'value', e.target.value)}
                        error={!!manager.valueErrors[index]}
                        helperText={manager.valueErrors[index]}
                        sx={{ flex: 1 }}
                      />
                    )}
                  </Box>
                  {isFixed ? (
                    <Box sx={{ width: '40px', flexShrink: 0 }} />
                  ) : (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => manager.remove(index)}
                      sx={deleteButtonSx}>
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '800px',
          minHeight: '550px',
          borderRadius: '12px',
          p: 3.75,
          gap: 3,
        },
      }}>
      <DialogTitle sx={{ p: 0 }}>
        <Typography component="div" sx={{ mb: 0.75, fontSize: '21px', fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ mb: 0.75, fontSize: '14px', color: 'text.secondary', fontWeight: 500 }}>
            Phone Number
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              setPhoneError('');
            }}
            placeholder="+12025550123"
            disabled={phoneNumberLocked}
            error={!!phoneError}
            helperText={phoneNumberLocked ? phoneNumberLockedHint : phoneError}
          />
        </Box>

        {renderKeyValueSection(
          attributesManager,
          t('phoneNumbers.editCustomerData.attributes.title'),
          t('phoneNumbers.editCustomerData.attributes.description'),
          t('phoneNumbers.editCustomerData.attributes.addButton'),
          true,
        )}

        <Divider sx={{ my: 3 }} />

        {renderKeyValueSection(
          metadataManager,
          t('phoneNumbers.editCustomerData.metadata.title'),
          t('phoneNumbers.editCustomerData.metadata.description'),
          t('phoneNumbers.editCustomerData.metadata.addButton'),
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleSave}
          variant="contained"
          size="medium"
          disabled={hasFormErrors()}
          sx={{ ...primaryButtonSx, width: '100px', height: '40px' }}>
          {saveLabel}
        </Button>
        <Button
          onClick={handleClose}
          size="medium"
          sx={{
            width: '100px',
            height: '40px',
            bgcolor: '#F2F6FA',
            color: '#2A3547',
            '&:hover': { bgcolor: '#DFE5EF', color: '#2A3547' },
          }}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditAttributesMetadataModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit'] as const),
  initialPhoneNumber: PropTypes.string,
  initialAttributes: PropTypes.object as PropTypes.Validator<Record<string, string> | undefined>,
  initialMetadata: PropTypes.object as PropTypes.Validator<Record<string, string> | undefined>,
  phoneNumberLocked: PropTypes.bool,
  phoneNumberLockedHint: PropTypes.string,
};
